import type { ReactNode, RefObject } from 'react'
import type { ChartContainer } from '../lib/useECharts'

/** Where an ECharts instance is drawn. The interface is two-thirds size on
 *  a desktop screen (CSS `zoom`, styles.css), and ECharts reads the pointer
 *  wrongly inside a zoomed element: at 0.67 the tooltip named a reading two
 *  hours from the one under the cursor. So the host cancels the zoom, and the
 *  chart is drawn full size and scaled down with a transform instead, which
 *  ECharts does read correctly. It looks exactly as the page around it is
 *  drawn, and the pointer lands where it is. At zoom 1 both are no-ops.
 *
 *  `children` are laid over the chart in its own pixels (the pin boxes):
 *  they sit inside the same scaled frame, so a position from ECharts'
 *  `convertToPixel` is right as it stands, at any zoom. */
export function ChartHost({ container, height, children, pinMode = false, showAppearance = false }: {
  container: RefObject<HTMLDivElement>
  height: number
  children?: ReactNode
  /** Pin mode's crosshair (styles.css, `.pin-mode`). */
  pinMode?: boolean
  /** Offer the line-appearance panel under the chart (width, points). Off
   *  unless a page asks: the charts that have their own Format button, and the
   *  station tools, do not want a second set of controls. */
  showAppearance?: boolean
}) {
  const presentation = (container as ChartContainer).presentation
  const options = presentation?.option.series
  const lines = (Array.isArray(options) ? options : options ? [options] : []).filter(series => (series.type === 'line' || series.type === 'scatter') && !series.silent)
  return (
    <>
    {showAppearance && presentation?.visible && lines.length > 0 && <details className="chart-appearance"><summary>Line appearance</summary><div className="chart-appearance-rows">{lines.map((series, index) => {
      if (series.type !== 'line' && series.type !== 'scatter') return null
      const key = String(series.id ?? series.name ?? index)
      const own = presentation.appearance[key] ?? { width: series.type === 'line' && typeof series.lineStyle?.width === 'number' ? series.lineStyle.width : 2, points: series.type === 'line' ? !!series.showSymbol : true, pointSize: typeof series.symbolSize === 'number' ? series.symbolSize : 4 }
      const change = (patch: Partial<typeof own>) => presentation.change({ ...presentation.appearance, [key]: { ...own, ...patch } })
      return <div key={key}><strong>{String(series.name || `Series ${index + 1}`)}</strong>{series.type === 'line' && <><label>Line width<select value={own.width} onChange={e => change({ width: Number(e.target.value) })}>{[1,1.5,2,3,4,6].map(value => <option key={value} value={value}>{value} px</option>)}</select></label><label className="chart-points"><input type="checkbox" checked={own.points} onChange={e => change({ points:e.target.checked })} />Points</label></>}<label>Point size<select value={own.pointSize} disabled={!own.points} onChange={e => change({ pointSize:Number(e.target.value) })}>{[2,3,4,6,8].map(value => <option key={value} value={value}>{value} px</option>)}</select></label></div>
    })}</div></details>}
    <div className="chart-host" style={{ height: `calc(${height}px * var(--ui-zoom, 1))` }}>
      <div className={pinMode ? 'chart-canvas pin-mode' : 'chart-canvas'} style={{ height }}>
        <div ref={container} style={{ height }} />
        {children}
      </div>
    </div>
    </>
  )
}
